// noinspection DuplicatedCode

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useMutation, useQuery } from 'react-query';
import { Input, MarkDownEditor } from 'components/Forms';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import PartnerProfileService from 'api/PartnerProfileService';
import {
  ActionButtons,
  BLOCK_EDITOR_DEFAULTS_PROPS_VALUE,
  BLOCK_EDITOR_PROPTYPES,
  ContentBlockEditor,
} from 'components/ContentBlockEditor';
import Alert from 'components/Alert/Alert';

const propTypes = BLOCK_EDITOR_PROPTYPES;
const defaultProps = BLOCK_EDITOR_DEFAULTS_PROPS_VALUE;

export default function HTMLBlockEditor(props) {
  const { contentBlock, onCancel, partnerId, onSaveSuccessfully } = props;
  const { handleSubmit, control, setValue, getValues } = useForm({
    defaultValues: contentBlock,
  });
  const mutationSaveBlock = useMutation(
    (block) => PartnerProfileService.addBlock(block),
    {
      onSuccess: (response) => {
        setTimeout(() => {
          onCancel();
          onSaveSuccessfully(response.data);
        }, 300);
      },
    }
  );
  const mutationUpdateBlock = useMutation(
    (block) => PartnerProfileService.updateBlock(block),
    {
      onSuccess: (response) => {
        const value = response.data.data[0];
        value.value = getValues().content;
        PartnerProfileService.updateBlockData(
          value.id,
          getValues().content
        ).then(() => {
          setTimeout(() => {
            onCancel();
            onSaveSuccessfully(response.data);
          }, 300);
        });
      },
    }
  );

  const { isLoading, refetch } = useQuery(
    ['contentBlock', contentBlock.id],
    () => PartnerProfileService.getContentBlock(contentBlock.id),
    {
      enabled: false,
      onSuccess: ({ data }) => {
        if (data) {
          setValue('id', data?.id);
          setValue('title', data?.title);
          setValue('content', data?.data[0]?.value?.toString());
          setValue('description', data?.description);
          setValue('displayOrder', data?.displayOrder);
        }
      },
    }
  );

  const onSubmit = (formData) => {
    const block = {
      id: formData.id,
      partnerId: formData.partnerId,
      contentType: 'html',
      title: formData.title,
      description: formData.description,
      displayOrder: formData.displayOrder,
      data: [
        {
          dataKey: 'content',
          value: formData.content,
          groupKey: '',
        },
      ],
    };
    if (contentBlock.id.toString().length > 0) {
      mutationUpdateBlock.mutate(block);
    } else {
      mutationSaveBlock.mutate(block);
    }
  };
  const isProcessing = () =>
    mutationSaveBlock.isLoading ||
    mutationSaveBlock.isSuccess ||
    mutationUpdateBlock.isLoading ||
    mutationUpdateBlock.isSuccess;
  const [processTypeLabel, setProcessTypeLabel] = useState('Add');
  const renderEditorForm = () => (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="d-block mb-2">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor="title" className="d-block mb-2">
          <strong>Block Title</strong>
        </label>
        <Input
          control={control}
          name="title"
          placeholder="Enter the block title"
          disabled={isProcessing()}
        />
      </div>
      <div className="d-block mb-2">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor="description" className="d-block mb-2">
          <strong>
            Block Description <i className="text-muted">(Optional)</i>
          </strong>
        </label>
        <Input
          name="description"
          control={control}
          placeholder="Enter the block description"
          disabled={isProcessing()}
        />
      </div>
      <div className="d-block mb-3">
        <MarkDownEditor
          name="content"
          control={control}
          placeholder="Enter block contents"
          disabled={isProcessing()}
        />
      </div>
      <div className="mb-0 text-end">
        <ActionButtons
          isProcessing={isProcessing()}
          onCancel={onCancel}
          isUpdate={contentBlock.id.toString().length > 0}
        />
      </div>
    </Form>
  );
  useEffect(() => {
    if (contentBlock) {
      setValue('id', contentBlock?.id);
      setValue('title', contentBlock?.title);
      if (contentBlock?.data && contentBlock?.data.length > 0) {
        setValue('content', contentBlock.data[0].value?.toString());
      }
      setValue('description', contentBlock?.description);
      setValue('displayOrder', contentBlock?.displayOrder);
      setProcessTypeLabel(
        contentBlock.id.toString().length > 0 ? 'Edit' : 'Add'
      );
      refetch();
    }
    setValue('partnerId', partnerId);
  }, [partnerId, contentBlock]);

  return (
    <ContentBlockEditor title={`${processTypeLabel} HTML Block`}>
      {mutationSaveBlock.isError || mutationUpdateBlock.error ? (
        <Alert variant="danger" data-testid="Alert-error">
          {mutationSaveBlock.isError
            ? mutationSaveBlock.error.message
            : mutationUpdateBlock.error.message}
          .
        </Alert>
      ) : null}
      {mutationSaveBlock.isSuccess && (
        <Alert variant="success" data-testid="Alert-addSuccess">
          New HTML Block is now added.
        </Alert>
      )}
      {mutationUpdateBlock.isSuccess && (
        <Alert variant="success" data-testid="Alert-updateSuccess">
          HTML Block has been updated.
        </Alert>
      )}
      {isLoading && contentBlock?.id?.length > 0 && (
        <div className="d-block">
          <Spinner size="sm" animation="border" /> Loading...
          <Button variant="link" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
      {(!isLoading || !contentBlock?.id) && renderEditorForm()}
    </ContentBlockEditor>
  );
}
HTMLBlockEditor.propTypes = propTypes;
HTMLBlockEditor.defaultProps = defaultProps;
